<script setup lang="ts">
import { PropType } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import { useUserStore } from '@/authentication/user.store'
import { toolkitUrls, urlHelpers } from '@/util/links.js'

export interface BlurPermissions {
  blurUnauthenticated?: boolean
  blurTrial?: boolean
  blurExpired?: boolean
}

defineProps({
  accessInfo: {
    type: Object as PropType<BlurPermissions>,
    required: true
  }
})

const { fetching, authInfo } = storeToRefs(useUserStore())

const redirectTo = (url: string) => {
  window.location.href = url
}
const signIn = () => redirectTo(urlHelpers.signInReturn(window.location.href))
const signUp = () => redirectTo(urlHelpers.signUpReturn('toolkit', window.location.href))
const openMembershipInformation = () => redirectTo(toolkitUrls.subscribe)

const alertClasses = 'mb-5 rounded-xs border p-[15px] text-md leading-5 [&>p+p]:mt-[5px] [&>p]:max-w-none'
const limitedClasses = `${alertClasses} border-[#d1e1ad] bg-green-100 text-[#666]`
const dangerClasses = `${alertClasses} border-[#ebccd1] bg-[#f2dede] text-red-300`
const headingClasses = 'mb-2.5 text-center text-xl font-medium leading-[1.1]'
</script>

<template>
  <div v-if="!fetching">
    <div v-if="accessInfo.blurUnauthenticated" :class="limitedClasses" data-testid="limit-access-unauthenticated">
      <h4 :class="headingClasses">You must sign in to view this entire resource</h4>
      <p class="flex items-center justify-center gap-1">
        <GbButton type="button" gbTheme="bluePrimary" size="small" label="Sign in" @click="signIn" />
        or
        <GbButton type="button" gbTheme="bluePrimary" size="small" label="Create an account" @click="signUp" />
      </p>
      <p>
        Creating an account also allows you to see alignment to state standards and Universal Design for Learning,
        adapted goals for varying levels of support, and related staircase goals by grade.
      </p>
    </div>

    <div v-if="accessInfo.blurTrial" :class="limitedClasses" data-testid="limit-access-trial">
      <h4 :class="headingClasses">This resource is available with a paid membership</h4>
      <p>Your trial membership allows you to view a sample of the resources Goalbook Toolkit has to offer.</p>
      <p class="text-center">
        <GbButton
          type="button"
          gbTheme="bluePrimary"
          size="small"
          label="Membership Information"
          @click="openMembershipInformation"
        />
      </p>
    </div>

    <div v-if="authInfo.isTrial && accessInfo.blurExpired" :class="dangerClasses" data-testid="limit-access-trial-expired">
      <h4 :class="headingClasses">Join premium membership to view the entire page</h4>
      <p>
        Your trial has expired. To view the entire page you must join premium membership.
        <a :href="toolkitUrls.subscribe" class="link" aria-label="Learn more about membership">Learn more</a>.
      </p>
    </div>

    <div v-if="authInfo.isPremium && accessInfo.blurExpired" :class="dangerClasses" data-testid="limit-access-premium-expired">
      <h4 :class="headingClasses">Your enterprise membership has lapsed</h4>
      <p>Your membership has ended. Please contact your administrator to renew your Toolkit enterprise membership.</p>
    </div>
  </div>
</template>
